/**
 * How the integration is doing, shown rather than described: the last run as a
 * pipeline from Scout to the records, the last 20 runs as a chart, the next
 * runs, each guard as a gauge, and where the data goes.
 */

import { Link, useNavigate } from "react-router-dom";
import {
  Bell,
  BellOff,
  CalendarClock,
  CalendarDays,
  ChartColumn,
  Clock,
  Globe,
  Hash,
  ListChecks,
  Send,
  ShieldCheck,
  TriangleAlert,
  Waypoints,
  Workflow,
} from "lucide-react";

import {
  Banner,
  Cards,
  Column,
  Facts,
  GuardGauges,
  Note,
  Panel,
  Pipeline,
  RunsChart,
  SkeletonRows,
  Tile,
  TonePill,
} from "@/components/kit";
import { getFields, listQueue, scoutJobUrl, type IntegrationDetail, type Run, type ScoutJobDetail } from "@/lib/integrations/api";
import { guardGauges, usualRows } from "@/lib/integrations/guards";
import { pipelineSteps } from "@/lib/integrations/pipeline";
import { presetOf, scheduleWords } from "@/lib/integrations/schedule";
import { normaliseSettings } from "@/lib/integrations/settings";
import { ago, durationWords, jobKindWords, plainError, runStatus, viaWords, when } from "@/lib/integrations/words";

import { useKeyed, type Keyed } from "../hooks";

export function OverviewTab({
  item,
  scoutJob,
  scoutError,
  now,
  runs,
}: {
  item: IntegrationDetail;
  scoutJob: ScoutJobDetail | undefined;
  scoutError?: string;
  now: number;
  runs: Keyed<Run[]>;
}) {
  const navigate = useNavigate();
  const fields = useKeyed(`fields|${item.code}`, () => getFields(item.code));
  const queue = useKeyed(`queue|${item.code}|${item.lastCheckedAt}`, () => listQueue(item.code, 5));
  const timeZone = item.eventTimezone ?? "UTC";
  const all = runs.data ?? [];
  const real = all.filter((run) => run.via !== "try");
  const last = real[0] ?? null;
  const chartRuns = real.slice(0, 20).reverse();
  const settings = normaliseSettings(item.live?.settings ?? item.draft?.settings ?? {});
  const newestScout = scoutJob?.lastRun ?? null;
  const steps = pipelineSteps({
    run: last,
    scoutNewest: newestScout ? { id: newestScout.id, status: newestScout.status, rows: newestScout.rows } : null,
    fields: fields.data?.fields ?? null,
    settings,
    reading: item.state === "on",
  });
  // The usual count before the last run, as the guard measured it then.
  const gauges = guardGauges(last?.counts ?? {}, last?.rowsCollected ?? 0, settings.safety, usualRows(real.slice(1)));
  const tripped = all.find((run) => run.guard);
  const schedule = scoutJob?.schedule ?? null;
  const newestJob = queue.data?.[0];
  const runHref = (run: Run) => `/integrations/${encodeURIComponent(item.code)}/runs/${run.id}`;

  const nextValue = !item.scoutJobId
    ? "No Scout job yet"
    : scoutJob === undefined && scoutError
      ? "Not known"
      : scoutJob === undefined
        ? "Reading Scout…"
        : schedule?.next_run_at
          ? when(schedule.next_run_at, timeZone)
          : "When a person asks";
  const nextNote = schedule?.timing
    ? `${scheduleWords(presetOf(schedule.timing), schedule.timezone ?? "UTC")}${schedule.timezone ? ` (${schedule.timezone})` : ""}${schedule.enabled === false ? " · switched off in Scout" : ""} · shown in ${timeZone} time`
    : scoutJob === undefined && scoutError
      ? "Scout did not describe this job, so its schedule is not known here."
      : scoutJob
        ? "Scout has no schedule for this job."
        : undefined;

  return (
    <Column>
      <Panel
        title="How the last run went"
        icon={<Workflow size={18} aria-hidden />}
        note={
          last
            ? `Run of ${when(last.createdAt, timeZone)} · ${viaWords(last.via)} · ${runStatus(last.status).word}`
            : "No run yet. This fills in once omnium reads a passed Scout run."
        }
        actions={last ? <Link to={runHref(last)}>Open this run</Link> : undefined}
      >
        {runs.error && <Banner tone="danger">{runs.error}</Banner>}
        {!runs.data && !runs.error ? (
          <SkeletonRows rows={2} columns={6} label="Reading the runs" />
        ) : (
          <Pipeline steps={steps} label="How the last run went, step by step" />
        )}
        <Note>
          Scout collects the rows. This integration keeps and maps them — set up on screens, with no code. The workflow’s own import then
          checks each row and writes the records.
        </Note>
      </Panel>

      <Panel
        title={chartRuns.length > 1 ? `Last ${chartRuns.length} runs` : "Runs"}
        icon={<ChartColumn size={18} aria-hidden />}
        actions={<Link to={`/integrations/${encodeURIComponent(item.code)}/runs`}>All runs</Link>}
      >
        {!runs.data ? (
          runs.error ? null : <SkeletonRows rows={2} columns={6} />
        ) : chartRuns.length === 0 ? (
          <Note>No runs yet. The first one shows here once Scout sends a passed run and omnium reads it.</Note>
        ) : (
          <RunsChart runs={chartRuns} timeZone={timeZone} onOpen={(run) => navigate(runHref(run))} />
        )}
      </Panel>

      <Cards>
        <Panel title="Next runs" icon={<CalendarClock size={18} aria-hidden />}>
          <Tile label="Next run" icon={<CalendarClock size={14} aria-hidden />} value={nextValue} note={nextNote} />
          <Facts
            items={[
              {
                key: "bell",
                icon: item.hasDoorbell ? <Bell size={14} aria-hidden /> : <BellOff size={14} aria-hidden />,
                content: item.hasDoorbell
                  ? "Scout rings the doorbell when a run passes, and omnium reads it at once."
                  : "No doorbell. omnium asks Scout for new runs by itself.",
              },
              {
                key: "check",
                icon: <ShieldCheck size={14} aria-hidden />,
                content: `If nothing comes in, omnium checks after ${durationWords(item.checkSeconds)}.`,
              },
              {
                key: "checked",
                icon: <Clock size={14} aria-hidden />,
                content: `Last checked ${ago(item.lastCheckedAt, now)} · last run ${ago(item.lastRunAt, now)}`,
              },
              ...(newestJob
                ? [
                    {
                      key: "queue",
                      icon: <ListChecks size={14} aria-hidden />,
                      content: `Queue: ${jobKindWords(newestJob.kind)} · ${newestJob.status === "done" ? `done ${ago(newestJob.finishedAt, now)}` : newestJob.status}${newestJob.error ? ` · ${plainError(newestJob.error)}` : ""}`,
                    },
                  ]
                : []),
            ]}
          />
        </Panel>

        <Panel
          title="Guards"
          icon={<ShieldCheck size={18} aria-hidden />}
          note={last ? "Measured on the last run. The dark line on each bar is where it pauses." : "Nothing measured yet."}
        >
          <GuardGauges gauges={gauges} />
          <Facts
            items={[
              {
                key: "window",
                icon: <CalendarDays size={14} aria-hidden />,
                content:
                  settings.safety.writeFrom || settings.safety.writeUntil
                    ? `Writes only between ${settings.safety.writeFrom ?? "any time"} and ${settings.safety.writeUntil ?? "any time"}.`
                    : "Writes at any time.",
              },
              {
                key: "tripped",
                icon: tripped ? <TriangleAlert size={14} aria-hidden /> : <ShieldCheck size={14} aria-hidden />,
                content: tripped
                  ? `A guard tripped ${ago(tripped.createdAt, now)}: ${tripped.message ?? tripped.guard}`
                  : "No guard has tripped in recent runs.",
              },
            ]}
          />
        </Panel>

        <Panel title="Where the data goes" icon={<Waypoints size={18} aria-hidden />}>
          <Facts
            labelled
            items={[
              {
                key: "scout",
                icon: <Globe size={14} aria-hidden />,
                label: "Scout job",
                content: item.scoutJobId ? (
                  <a href={scoutJobUrl(item.scoutJobId)} target="_blank" rel="noreferrer">
                    {scoutJob?.name ?? item.scoutJobId}
                  </a>
                ) : (
                  "None yet"
                ),
              },
              {
                key: "workflow",
                icon: <Workflow size={14} aria-hidden />,
                label: "Workflow",
                content: <Link to={`/workflows/${encodeURIComponent(item.workflow)}`}>{item.workflow}</Link>,
              },
              { key: "import", icon: <Send size={14} aria-hidden />, label: "Import", content: item.importCommand },
              {
                key: "event",
                icon: <CalendarDays size={14} aria-hidden />,
                label: "Event",
                content: (
                  <>
                    <Link to={`/events/${encodeURIComponent(item.eventCode)}`}>{item.eventName}</Link> ({timeZone})
                  </>
                ),
              },
              { key: "ids", icon: <Hash size={14} aria-hidden />, label: "Their ids", content: settings.match.idSource ?? item.code },
              {
                key: "bell",
                icon: <Bell size={14} aria-hidden />,
                label: "Doorbell",
                content: item.hasDoorbell ? <TonePill tone="good">Connected</TonePill> : <TonePill tone="neutral">Not connected</TonePill>,
              },
            ]}
          />
        </Panel>
      </Cards>
    </Column>
  );
}
